import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { Button, Field, Input, Select, Spinner, humanise } from './ui.js'

export type PickedClient = RouterOutputs['clients']['search'][number]

/** Which kind of record the office is about to create, or nothing yet. */
type CreateKind = 'INDIVIDUAL' | 'BUSINESS'

/** The company-ish half of `ClientType`. An individual is always residential. */
const BUSINESS_TYPES = ['COMMERCIAL', 'STRATA', 'GOVERNMENT', 'REAL_ESTATE'] as const

/**
 * Search, select, or create — in that order and without leaving the screen.
 *
 * Every client is the same record. An **individual** is one whose `name` is a
 * person's; a **business** is one whose `name` is a company's. Both carry
 * `Contact`s, so creating either writes a contact as well: for an individual
 * that is the same person again, and for a business it is whoever the job came
 * from. Decision 60.
 *
 * The two create buttons stay on screen while there are results, because a
 * near-match is not a match — "Tom Smith" already existing does not mean this
 * Tom Smith is that one.
 */
export function ClientPicker({
  id = 'client-picker',
  value,
  onChange,
  autoFocus,
}: {
  id?: string
  value: PickedClient | null
  onChange: (client: PickedClient | null) => void
  autoFocus?: boolean
}) {
  const trpc = useTRPC()
  const { can } = useSession()

  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState<CreateKind | null>(null)

  const hits = useQuery({
    ...trpc.clients.search.queryOptions({ q: query }),
    enabled: query.length >= 2,
  })

  if (value) {
    return (
      <div className="flex items-center gap-2.5 rounded-md bg-ink-50 px-2.5 py-2 ring-1 ring-inset ring-ink-200">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink-900">{value.name}</span>
          <span className="block text-xs text-ink-500">
            #{value.number} · {humanise(value.type)}
          </span>
        </span>
        <Button size="sm" type="button" onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    )
  }

  if (creating) {
    return (
      <NewClientPanel
        kind={creating}
        typed={query}
        onCancel={() => setCreating(null)}
        onCreated={(client) => {
          setCreating(null)
          setQuery('')
          onChange(client)
        }}
      />
    )
  }

  return (
    <Field label="Client" htmlFor={id} required>
      <Input
        id={id}
        autoFocus={autoFocus}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        // This picker is mounted inside other forms, so Enter here would
        // submit the job rather than the search it looks like it runs.
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.preventDefault()
        }}
        placeholder="Start typing a name, phone or email…"
      />

      <ClientResults
        query={query}
        loading={hits.isFetching}
        hits={hits.data ?? []}
        onPick={(hit) => {
          setQuery('')
          onChange(hit)
        }}
      />

      {can('CLIENT_EDIT') ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-ink-500">Not on the list?</span>
          <Button size="sm" type="button" onClick={() => setCreating('INDIVIDUAL')}>
            New individual
          </Button>
          <Button size="sm" type="button" onClick={() => setCreating('BUSINESS')}>
            New business
          </Button>
        </div>
      ) : null}
    </Field>
  )
}

function ClientResults({
  query,
  loading,
  hits,
  onPick,
}: {
  query: string
  loading: boolean
  hits: PickedClient[]
  onPick: (hit: PickedClient) => void
}) {
  if (query.length < 2) {
    return <p className="text-xs text-ink-400">Two letters is enough to start looking.</p>
  }

  if (loading && hits.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-ink-400">
        <Spinner tone="brand" className="size-3" /> Looking…
      </p>
    )
  }

  if (hits.length === 0) {
    return <p className="text-xs text-ink-500">Nobody by that name yet.</p>
  }

  return (
    <ul className="max-h-44 overflow-y-auto rounded-md ring-1 ring-inset ring-ink-200">
      {hits.map((hit) => (
        <li key={hit.id}>
          <button
            type="button"
            onClick={() => onPick(hit)}
            className="flex min-h-[var(--control-sm)] w-full items-center gap-2 px-2.5 text-left text-[13px] transition-colors hover:bg-ink-50"
          >
            <span className="min-w-0 flex-1 truncate text-ink-900">{hit.name}</span>
            <span className="shrink-0 font-mono text-[11px] text-ink-400">#{hit.number}</span>
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * The create half, prefilled from whatever was typed into the search.
 *
 * It is a `div` and not a `form`: the picker is mounted inside the New job
 * form and inside the job card, and a nested form is invalid markup whose
 * submit escapes to the outer one. Enter is caught here instead, so the key
 * still does what the panel looks like it should do.
 */
function NewClientPanel({
  kind,
  typed,
  onCancel,
  onCreated,
}: {
  kind: CreateKind
  typed: string
  onCancel: () => void
  onCreated: (client: PickedClient) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const individual = kind === 'INDIVIDUAL'
  const [first, last] = splitName(typed)

  const [businessName, setBusinessName] = useState(individual ? '' : typed)
  const [type, setType] = useState<(typeof BUSINESS_TYPES)[number]>('COMMERCIAL')
  const [firstName, setFirstName] = useState(individual ? first : '')
  const [lastName, setLastName] = useState(individual ? last : '')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')

  const create = useMutation(
    trpc.clients.create.mutationOptions({
      onSuccess: (client) => {
        void queryClient.invalidateQueries(trpc.clients.pathFilter())
        onCreated(client)
      },
    }),
  )

  // An individual's client name is the person's, so the two cannot disagree.
  const name = individual ? [firstName.trim(), lastName.trim()].filter(Boolean).join(' ') : businessName.trim()
  // A business may be all the office has; a person needs a name to be a person.
  const ready = individual ? firstName.trim().length > 0 : businessName.trim().length > 0

  function submit() {
    if (!ready || create.isPending) return

    const contact = firstName.trim()
      ? {
          firstName: firstName.trim(),
          lastName: lastName.trim() || null,
          email: email.trim() || null,
          mobile: mobile.trim() || null,
        }
      : null

    create.mutate({
      name,
      type: individual ? 'RESIDENTIAL' : type,
      // For an individual the client *is* the person, so their details belong
      // on both records — the client list and search read the client's.
      ...(individual ? { email: email.trim() || null, phone: mobile.trim() || null } : {}),
      ...(contact ? { primaryContact: contact } : {}),
    })
  }

  return (
    <div
      className="flex flex-col gap-3 rounded-md bg-ink-50 p-2.5 ring-1 ring-inset ring-ink-200"
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return
        event.preventDefault()
        submit()
      }}
    >
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-semibold text-ink-900">
          {individual ? 'New individual' : 'New business'}
        </h3>
        <p className="min-w-0 flex-1 truncate text-xs text-ink-500">
          {individual
            ? 'One person. They become the client and its first contact.'
            : 'The company is the client. Add whoever the job came from.'}
        </p>
        <Button size="sm" type="button" onClick={onCancel}>
          Back
        </Button>
      </div>

      {individual ? null : (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Business name" htmlFor="new-client-business" required>
            <Input
              id="new-client-business"
              autoFocus
              value={businessName}
              onChange={(event) => setBusinessName(event.target.value)}
              placeholder="Trading or company name"
            />
          </Field>
          <Field label="Kind of business" htmlFor="new-client-type">
            <Select
              id="new-client-type"
              value={type}
              onChange={(event) => setType(event.target.value as (typeof BUSINESS_TYPES)[number])}
            >
              {BUSINESS_TYPES.map((option) => (
                <option key={option} value={option}>
                  {humanise(option)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}

      {individual ? null : (
        <p className="text-xs font-medium text-ink-700">
          First contact <span className="font-normal text-ink-500">— the person you got the job from</span>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="First name" htmlFor="new-client-first" required={individual}>
          <Input
            id="new-client-first"
            autoFocus={individual}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            placeholder="Tom"
          />
        </Field>
        <Field label="Last name" htmlFor="new-client-last">
          <Input
            id="new-client-last"
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            placeholder="Smith"
          />
        </Field>
        <Field label="Email" htmlFor="new-client-email">
          <Input
            id="new-client-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="tom@example.com"
          />
        </Field>
        <Field label="Mobile" htmlFor="new-client-mobile">
          <Input
            id="new-client-mobile"
            type="tel"
            value={mobile}
            onChange={(event) => setMobile(event.target.value)}
            placeholder="0400 000 000"
          />
        </Field>
      </div>

      {create.error ? (
        <p className="text-xs text-red-600" role="alert">
          {errorMessage(create.error)}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-xs text-ink-500">
          {name ? `Saved as ${name}` : 'The rest can be filled in on the client screen.'}
        </p>
        <Button
          type="button"
          variant="primary"
          size="sm"
          loading={create.isPending}
          disabled={!ready}
          onClick={submit}
        >
          {individual ? 'Create individual' : 'Create business'}
        </Button>
      </div>
    </div>
  )
}

/**
 * "Tom Smith" into a first and a last name.
 *
 * The last space wins, so "Mary Anne O'Brien" keeps the surname whole. It is a
 * starting point rather than an answer: both halves are editable fields, which
 * is the point of showing them rather than splitting silently on save.
 */
function splitName(typed: string): [string, string] {
  const trimmed = typed.trim().replace(/\s+/g, ' ')
  const cut = trimmed.lastIndexOf(' ')
  return cut === -1 ? [trimmed, ''] : [trimmed.slice(0, cut), trimmed.slice(cut + 1)]
}
